import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Pencil, Plus, ShieldOff, UserCheck } from 'lucide-react';
import { AppError } from '@/core/errors/AppError';
import { useSession } from '@/core/auth/useSession';
import { useCambiarEstadoUsuario, useGruposAdmin, useUsuariosAdmin, } from '@/data/hooks/useAdmin';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { formatearFechaHora, iniciales } from '@/core/utils/formatters';
import { Button } from '@/shared/components/Button';
import { Chip } from '@/shared/components/Chip';
import { ConfirmDialog } from '@/shared/components/ConfirmDialog';
import { Pagination } from '@/shared/components/Pagination';
import { SearchInput } from '@/shared/components/SearchInput';
import { Select } from '@/shared/components/Select';
import { Table } from '@/shared/components/Table';
import { useToast } from '@/shared/components/Toast';
import { UsuarioFormDialog } from './UsuarioFormDialog';
const POR_PAGINA = 20;
const PERSONA_TIPO_LABEL = {
    INSTRUCTOR: 'Instructor',
    PROMOTOR: 'Promotor',
};
/**
 * Mapea el nombre del grupo a un tono visual. Cubre los grupos del sistema;
 * cualquier grupo custom cae en `info` por defecto.
 */
function tonoDeGrupo(nombre) {
    const n = nombre.toLowerCase();
    if (n.includes('super'))
        return 'primary';
    if (n.includes('admin'))
        return 'primary';
    if (n.includes('instructor'))
        return 'info';
    if (n.includes('externo'))
        return 'warning';
    if (n.includes('promotor'))
        return 'success';
    if (n.includes('recepción') || n.includes('recepcion') || n.includes('caja'))
        return 'info';
    return 'neutral';
}
export function UsuariosAdminPage() {
    const session = useSession();
    const { tieneRol } = session;
    const puedeAdmin = tieneRol('ROLE_ADMINISTRAR_USUARIO');
    const toast = useToast();
    const [busq, setBusq] = useState('');
    const [estado, setEstado] = useState('activos');
    const [grupoId, setGrupoId] = useState('');
    const [pagina, setPagina] = useState(1);
    const q = useDebounce(busq, 300);
    const { data: grupos } = useGruposAdmin();
    const { data, isLoading } = useUsuariosAdmin({
        q: q || undefined,
        activo: estado === 'todos' ? undefined : estado === 'activos',
        grupoId: grupoId || undefined,
        pagina,
        porPagina: POR_PAGINA,
    });
    const cambiarEstado = useCambiarEstadoUsuario();
    const [editable, setEditable] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [desactivando, setDesactivando] = useState(null);
    const ejecutarCambioEstado = (u) => {
        cambiarEstado.mutate({ id: u.id, activo: !u.activo }, {
            onSuccess: () => {
                toast.exito(u.activo ? 'Usuario desactivado' : 'Usuario reactivado');
                setDesactivando(null);
            },
            onError: (err) => toast.error(err instanceof AppError ? err.mensaje : 'No pudimos cambiar.'),
        });
    };
    const sessionUserId = session.usuario?.id ?? null;
    const columnas = useMemo(() => [
        {
            key: 'usuario',
            header: 'Usuario',
            cell: (u) => (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("div", { className: "flex h-7 w-7 items-center justify-center rounded-full bg-primary-light text-[11px] font-semibold text-primary", children: iniciales(`${u.nombre} ${u.apellido}`) }), _jsxs("div", { className: "flex flex-col", children: [_jsxs("span", { className: "font-medium", children: [u.nombre, " ", u.apellido] }), _jsx("span", { className: "text-xs text-text-tertiary", children: u.email })] })] })),
        },
        {
            key: 'grupos',
            header: 'Grupos',
            cell: (u) => u.grupos.length === 0 ? (_jsx("span", { className: "text-text-tertiary", children: "\u2014" })) : (_jsx("div", { className: "flex flex-wrap gap-1", children: u.grupos.map((g) => (_jsx(Chip, { tono: tonoDeGrupo(g.nombre), children: g.nombre }, g.id))) })),
        },
        {
            key: 'persona',
            header: 'Vínculo',
            cell: (u) => u.personaTipo && u.personaNombre ? (_jsxs("div", { className: "flex flex-col leading-tight", children: [_jsx("span", { className: "text-sm", children: u.personaNombre }), _jsx("span", { className: "text-[10px] text-text-tertiary", children: PERSONA_TIPO_LABEL[u.personaTipo] ?? u.personaTipo })] })) : (_jsx("span", { className: "text-text-tertiary", children: "\u2014" })),
            width: '180px',
        },
        {
            key: 'ultimo',
            header: 'Último login',
            cell: (u) => (u.ultimoLogin ? formatearFechaHora(u.ultimoLogin) : 'Nunca'),
            width: '180px',
        },
        {
            key: 'estado',
            header: 'Estado',
            width: '110px',
            cell: (u) => u.activo ? _jsx(Chip, { tono: "success", children: "Activo" }) : _jsx(Chip, { tono: "neutral", children: "Inactivo" }),
        },
        ...(puedeAdmin
            ? [
                {
                    key: 'acc',
                    header: '',
                    width: '120px',
                    align: 'right',
                    cell: (u) => {
                        const esSelf = sessionUserId !== null && u.id === sessionUserId;
                        const desactivarBloqueado = u.activo && esSelf;
                        return (_jsxs("div", { className: "flex items-center justify-end gap-1", children: [_jsx(Button, { variant: "ghost", size: "icon", onClick: () => {
                                        setEditable(u);
                                        setFormOpen(true);
                                    }, "aria-label": "Editar", children: _jsx(Pencil, { className: "h-3.5 w-3.5" }) }), _jsx(Button, { variant: "ghost", size: "icon", disabled: desactivarBloqueado, onClick: () => {
                                        if (u.activo)
                                            setDesactivando(u);
                                        else
                                            ejecutarCambioEstado(u);
                                    }, "aria-label": u.activo ? 'Desactivar' : 'Reactivar', title: desactivarBloqueado
                                        ? 'No podés desactivarte a vos mismo.'
                                        : u.activo
                                            ? 'Desactivar usuario'
                                            : 'Reactivar usuario', className: desactivarBloqueado
                                        ? 'cursor-not-allowed opacity-40'
                                        : u.activo
                                            ? 'text-danger'
                                            : 'text-success', children: u.activo ? (_jsx(ShieldOff, { className: "h-3.5 w-3.5" })) : (_jsx(UserCheck, { className: "h-3.5 w-3.5" })) })] }));
                    },
                },
            ]
            : []),
    ], [puedeAdmin, sessionUserId]);
    return (_jsxs("div", { className: "flex flex-col gap-4", children: [_jsxs("div", { className: "flex flex-wrap items-start justify-between gap-3", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-xl font-bold text-text-primary", children: "Usuarios" }), _jsx("p", { className: "text-xs text-text-secondary", children: "Gesti\u00F3n de usuarios con acceso al sistema y sus grupos." })] }), puedeAdmin ? (_jsxs(Button, { onClick: () => {
                            setEditable(null);
                            setFormOpen(true);
                        }, children: [_jsx(Plus, { className: "h-4 w-4" }), "Nuevo usuario"] })) : null] }), _jsxs("div", { className: "flex flex-wrap items-end gap-2", children: [_jsx(SearchInput, { value: busq, onChange: (v) => {
                            setBusq(v);
                            setPagina(1);
                        }, placeholder: "Buscar por email, nombre o apellido\u2026" }), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[11px] uppercase tracking-wide text-text-tertiary", children: "Grupo" }), _jsxs(Select, { value: grupoId, onChange: (e) => {
                                    setGrupoId(e.target.value);
                                    setPagina(1);
                                }, className: "w-48", children: [_jsx("option", { value: "", children: "Todos los grupos" }), grupos?.map((g) => (_jsx("option", { value: g.id, children: g.nombre }, g.id)))] })] }), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[11px] uppercase tracking-wide text-text-tertiary", children: "Estado" }), _jsxs(Select, { value: estado, onChange: (e) => {
                                    setEstado(e.target.value);
                                    setPagina(1);
                                }, className: "w-36", children: [_jsx("option", { value: "activos", children: "Activos" }), _jsx("option", { value: "inactivos", children: "Inactivos" }), _jsx("option", { value: "todos", children: "Todos" })] })] })] }), _jsx("p", { className: "text-[11px] text-text-tertiary", children: "Ordenado por fecha de alta \u2014 los m\u00E1s recientes arriba." }), _jsx(Table, { columns: columnas, items: data?.items ?? [], rowKey: (u) => u.id, cargando: isLoading, vacioTitulo: "Sin usuarios" }), data ? (_jsx(Pagination, { pagina: data.paginacion.pagina, totalPaginas: data.paginacion.totalPaginas, total: data.paginacion.total, porPagina: data.paginacion.porPagina, onCambioPagina: setPagina })) : null, _jsx(UsuarioFormDialog, { open: formOpen, onOpenChange: setFormOpen, usuario: editable }), _jsx(ConfirmDialog, { open: Boolean(desactivando), onOpenChange: (open) => !open && setDesactivando(null), titulo: "Desactivar usuario", descripcion: desactivando
                    ? `"${desactivando.nombre} ${desactivando.apellido}" no podrá iniciar sesión. Las acciones previas (reservas, pagos) se mantienen registradas a su nombre.`
                    : undefined, textoConfirmar: "Desactivar", destructivo: true, cargando: cambiarEstado.isPending, onConfirmar: () => desactivando && ejecutarCambioEstado(desactivando) })] }));
}
